// SUPPORT1 (docs/build-briefs/SUPPORT1-get-help-and-support.md; owner ruling DEC-514 item 4). Get help, Report a problem and
// My requests, drawn once and shared by the location, franchisor and Snip & Sip Admin consoles (mockups
// design/mockups/entbasics/support1-get-help-and-support.html, frames 3 to 6). Each console hands in its own request helper (its own
// bearer token and base path) and its own way to name the screen the person is on; nothing here reaches another console's
// session. There is no entry on the floor: only the three consoles' account menus call this.
//
// Every shown value is set with textContent. The markup holds no value from the server or the person. A message is plain text.

import { showConsoleToast } from "./consoleToast.ts";
import {
  BODY_MAX,
  CODE_PATTERN,
  TITLE_MAX,
  countLabel,
  customerChip,
  failureSentence,
  formatWhen,
  listWhen,
  newReplyWords,
  reportErrors,
  typeWord,
  type Chip,
  type CustomerRequestDetailDto,
  type CustomerRequestSummaryDto,
  type SupportType,
} from "./supportHelpModel.ts";

export interface SupportReply<T> {
  readonly status: number;
  readonly body: T | null;
}

export interface SupportHelpOptions {
  /** One call to this console's support routes. `suffix` is "", "/summary", "/<id>" or "/<id>/messages". */
  readonly request: <T>(method: "GET" | "POST", suffix: string, body?: unknown) => Promise<SupportReply<T>>;
  /** The screen the person is on: a path (no ids) and the page name they read. */
  readonly screen: () => { route: string; label: string };
  /** The location the person works at, when the console has one. */
  readonly locationName: () => string | null;
  /** The build revision baked into this bundle. */
  readonly buildRevision: () => string;
  /** The count badge inside the account menu row, when it exists. */
  readonly badge: () => HTMLElement | null;
}

/** AIHELP1: text the form opens with. The person still reads, edits and sends it; nothing is sent for them. */
export interface SupportPrefill {
  readonly title?: string;
  readonly body?: string;
}

export interface SupportHelp {
  /** Opens the Get help sheet. */
  openSheet(): void;
  /** Opens My requests, with one request selected when its id is given. */
  openMyRequests(requestId?: string): void;
  /** Opens the form for a new request of this type, with the error code when the person has just seen one. */
  openForm(type: SupportType, prefill?: SupportPrefill): void;
  /** AIHELP1: Willy's "Contact support". Reads the reply email first, then opens the question form with the conversation in it. */
  openPrefilled(type: SupportType, prefill: SupportPrefill): void;
  /** Re-reads the count of replies waiting and draws the badge. */
  refresh(): Promise<void>;
  /** Signed out: closes everything and clears what was read. */
  reset(): void;
}

interface Summary {
  unreadReplies: number;
  statusUrl: string | null;
  supportEmail: string | null;
  email: string | null;
}

const ERROR_NOTICE_CODE = '#error-notice [data-testid="failure-state-code"]';

function make<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function chipNode(chip: Chip): HTMLElement {
  const node = make("span", `fl-chip fl-chip-${chip.tone} support-chip`);
  if (chip.symbol !== "") node.append(make("span", "fl-chip-symbol", chip.symbol), " ");
  node.append(chip.words);
  return node;
}

/** The text of the first element matching `selector` that is on screen (not hidden by `hidden` or an ancestor), or null. */
export function firstVisibleText(selector: string, doc: Document = document): string | null {
  for (const node of Array.from(doc.querySelectorAll<HTMLElement>(selector))) {
    if (node.offsetParent !== null && (node.textContent ?? "").trim() !== "") return node.textContent;
  }
  return null;
}

/** The name of the screen on show: the first visible heading, else the label of the first visible screen section. */
export function visibleScreenName(headingSelector: string, doc: Document = document): string | null {
  const heading = firstVisibleText(headingSelector, doc);
  if (heading !== null) return heading;
  for (const node of Array.from(doc.querySelectorAll<HTMLElement>("section[aria-label]"))) {
    if (node.getClientRects().length > 0) return node.getAttribute("aria-label");
  }
  return null;
}

/** The error code on screen right now (ERRTRACK1's notice), or null. Read from the notice that module draws. */
export function visibleErrorCode(doc: Document = document): string | null {
  const text = doc.querySelector(ERROR_NOTICE_CODE)?.textContent?.trim().toUpperCase() ?? "";
  return CODE_PATTERN.test(text) ? text : null;
}

export function createSupportHelp(options: SupportHelpOptions): SupportHelp {
  let summary: Summary = { unreadReplies: 0, statusUrl: null, supportEmail: null, email: null };
  let sheet: HTMLElement | null = null;
  let page: HTMLElement | null = null;
  let returnFocus: Element | null = null;
  let selectedId: string | null = null;
  let list: CustomerRequestSummaryDto[] = [];
  let detail: CustomerRequestDetailDto | null = null;
  let loadToken = 0;

  const drawBadge = (): void => {
    const node = options.badge();
    if (node === null) return;
    node.textContent = summary.unreadReplies > 0 ? String(summary.unreadReplies) : "";
    node.hidden = summary.unreadReplies === 0;
    node.setAttribute("aria-label", summary.unreadReplies > 0 ? newReplyWords(summary.unreadReplies) : "");
  };

  async function refresh(): Promise<void> {
    const reply = await options.request<Summary>("GET", "/summary");
    if (reply.status === 200 && reply.body !== null) {
      summary = reply.body;
      drawBadge();
    }
  }

  // ---- the scrim that holds a sheet ---------------------------------------------------------------------------------------

  function closeSheet(): void {
    sheet?.remove();
    sheet = null;
    const back = returnFocus;
    returnFocus = null;
    if (back instanceof HTMLElement) back.focus();
  }

  function openScrim(label: string, width: number): { body: HTMLElement; foot: HTMLElement; title: HTMLElement; close: () => void } {
    closeSheet();
    returnFocus = document.activeElement;
    const scrim = make("div", "scrim support-scrim");
    scrim.dataset["testid"] = "support-scrim";
    const card = make("div", "sheet support-sheet");
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-modal", "true");
    card.setAttribute("aria-label", label);
    card.style.maxWidth = `${width}px`;
    const head = make("div", "support-sheet-head");
    const title = make("h2", "support-sheet-title", label);
    title.tabIndex = -1;
    const closeButton = make("button", "btn-secondary btn-compact", "Close");
    closeButton.type = "button";
    closeButton.dataset["testid"] = "support-close";
    head.append(title, closeButton);
    const body = make("div", "support-sheet-body");
    const foot = make("div", "support-sheet-foot");
    card.append(head, body, foot);
    scrim.append(card);
    const close = (): void => closeSheet();
    closeButton.addEventListener("click", close);
    scrim.addEventListener("click", (event) => {
      if (event.target === scrim) close();
    });
    scrim.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    });
    document.body.append(scrim);
    sheet = scrim;
    title.focus();
    return { body, foot, title, close };
  }

  // ---- Get help -----------------------------------------------------------------------------------------------------------

  function choiceRow(title: string, help: string, testid: string, chip?: string): HTMLButtonElement {
    const row = make("button", "support-choice");
    row.type = "button";
    row.dataset["testid"] = testid;
    const text = make("span", "support-choice-text");
    text.append(make("b", "support-choice-title", title), make("span", "support-help", help));
    row.append(text);
    if (chip !== undefined) row.append(make("span", "support-count", chip));
    row.append(make("span", "support-chevron", "›"));
    return row;
  }

  function openSheet(): void {
    const { body, close } = openScrim("Get help", 560);
    const report = choiceRow("Report a problem", "Something is broken or looks wrong", "support-choice-report");
    const ask = choiceRow("Ask a question", "You are stuck and want an answer", "support-choice-ask");
    const mine = choiceRow(
      "My requests",
      "See replies and past requests",
      "support-choice-mine",
      summary.unreadReplies > 0 ? newReplyWords(summary.unreadReplies) : undefined,
    );
    body.append(report, ask, mine);
    if (summary.statusUrl !== null) {
      const status = make("a", "support-choice");
      status.href = summary.statusUrl;
      status.target = "_blank";
      status.rel = "noopener noreferrer";
      status.dataset["testid"] = "support-choice-status";
      const text = make("span", "support-choice-text");
      text.append(make("b", "support-choice-title", "System status"), make("span", "support-help", "Is Snip & Sip running normally? Opens in a new tab."));
      status.append(text, make("span", "support-chevron", "›"));
      body.append(status);
    }
    if (summary.supportEmail !== null) {
      const note = make("p", "support-note", `Or email ${summary.supportEmail}`);
      note.dataset["testid"] = "support-email-note";
      body.append(note);
    }
    report.addEventListener("click", () => {
      close();
      openForm("problem");
    });
    ask.addEventListener("click", () => {
      close();
      openForm("question");
    });
    mine.addEventListener("click", () => {
      close();
      openMyRequests();
    });
  }

  // ---- the form -----------------------------------------------------------------------------------------------------------

  function openForm(initialType: SupportType, prefill?: SupportPrefill): void {
    const screen = options.screen();
    const code = visibleErrorCode();
    let type: SupportType = initialType;
    const { body, foot, title, close } = openScrim(initialType === "problem" ? "Report a problem" : "Ask a question", 640);
    const idempotencyKey = crypto.randomUUID();

    const typeField = make("div", "support-field");
    const typeLabel = make("span", "field-label", "What is this about?");
    const seg = make("div", "support-seg");
    seg.setAttribute("role", "group");
    seg.setAttribute("aria-label", "What is this about?");
    const problemButton = make("button", "", "Report a problem");
    const questionButton = make("button", "", "Ask a question");
    for (const button of [problemButton, questionButton]) button.type = "button";
    problemButton.dataset["testid"] = "support-type-problem";
    questionButton.dataset["testid"] = "support-type-question";
    seg.append(problemButton, questionButton);
    typeField.append(typeLabel, seg);

    const titleField = make("div", "support-field");
    const titleLabel = make("label", "field-label", "Short title");
    titleLabel.htmlFor = "support-title";
    const titleInput = make("input", "field-input");
    titleInput.id = "support-title";
    titleInput.type = "text";
    titleInput.maxLength = TITLE_MAX;
    titleInput.inputMode = "text";
    titleInput.autocomplete = "off";
    titleInput.dataset["testid"] = "support-title";
    const titleError = make("p", "support-error");
    titleError.hidden = true;
    titleError.setAttribute("role", "alert");
    titleError.dataset["testid"] = "support-title-error";
    titleField.append(titleLabel, titleInput, titleError);

    const bodyField = make("div", "support-field");
    const bodyLabel = make("label", "field-label", "What happened?");
    bodyLabel.htmlFor = "support-body";
    const bodyInput = make("textarea", "field-input");
    bodyInput.id = "support-body";
    bodyInput.maxLength = BODY_MAX;
    bodyInput.rows = 4;
    bodyInput.inputMode = "text";
    bodyInput.dataset["testid"] = "support-body";
    const bodyMeta = make("div", "support-meta");
    bodyMeta.append(make("p", "support-help", "Do not type card numbers or PINs. Long numbers are hidden before we save."));
    const counter = make("p", "support-help", countLabel(0));
    counter.dataset["testid"] = "support-count";
    bodyMeta.append(counter);
    const bodyError = make("p", "support-error");
    bodyError.hidden = true;
    bodyError.setAttribute("role", "alert");
    bodyError.dataset["testid"] = "support-body-error";
    bodyField.append(bodyLabel, bodyInput, bodyMeta, bodyError);

    const emailField = make("div", "support-field");
    const emailLabel = make("label", "field-label", "We can reply to");
    emailLabel.htmlFor = "support-email";
    const emailInput = make("input", "field-input");
    emailInput.id = "support-email";
    emailInput.type = "email";
    emailInput.inputMode = "email";
    emailInput.autocomplete = "email";
    emailInput.value = summary.email ?? "";
    emailInput.dataset["testid"] = "support-email";
    const emailError = make("p", "support-error");
    emailError.hidden = true;
    emailError.setAttribute("role", "alert");
    emailError.dataset["testid"] = "support-email-error";
    emailField.append(emailLabel, emailInput, emailError);

    // SUPCTX1 (Kerry, 2026-10-27): page, location, build and code are still sent with the request, just not shown.

    const general = make("p", "support-error support-error-general");
    general.hidden = true;
    general.setAttribute("role", "alert");
    general.dataset["testid"] = "support-error";

    body.append(typeField, titleField, bodyField, emailField, general);

    const cancel = make("button", "btn-secondary", "Cancel");
    cancel.type = "button";
    cancel.dataset["testid"] = "support-cancel";
    const send = make("button", "btn-primary", "Send");
    send.type = "button";
    send.dataset["testid"] = "support-send";
    foot.append(cancel, send);

    const drawType = (): void => {
      problemButton.setAttribute("aria-pressed", String(type === "problem"));
      questionButton.setAttribute("aria-pressed", String(type === "question"));
      title.textContent = type === "problem" ? "Report a problem" : "Ask a question";
      bodyLabel.textContent = type === "problem" ? "What happened?" : "What do you need to know?";
      sheet?.querySelector(".support-sheet")?.setAttribute("aria-label", title.textContent);
    };
    problemButton.addEventListener("click", () => {
      type = "problem";
      drawType();
    });
    questionButton.addEventListener("click", () => {
      type = "question";
      drawType();
    });
    drawType();
    if (prefill?.title !== undefined) titleInput.value = prefill.title.slice(0, TITLE_MAX);
    if (prefill?.body !== undefined) {
      bodyInput.value = prefill.body.slice(0, BODY_MAX);
      counter.textContent = countLabel(bodyInput.value.length);
    }
    bodyInput.addEventListener("input", () => {
      counter.textContent = countLabel(bodyInput.value.length);
    });
    cancel.addEventListener("click", close);

    let sending = false;
    send.addEventListener("click", () => {
      if (sending) return;
      const errors = reportErrors({ title: titleInput.value, body: bodyInput.value, email: emailInput.value });
      for (const [node, text] of [
        [titleError, errors.title],
        [bodyError, errors.body],
        [emailError, errors.email],
      ] as const) {
        node.textContent = text ?? "";
        node.hidden = text === undefined;
      }
      general.hidden = true;
      if (errors.title !== undefined || errors.body !== undefined || errors.email !== undefined) return;
      sending = true;
      send.disabled = true;
      void (async () => {
        const reply = await options.request<{ request: CustomerRequestDetailDto }>("POST", "", {
          requestType: type,
          title: titleInput.value,
          body: bodyInput.value,
          contactEmail: emailInput.value.trim(),
          screenRoute: screen.route,
          screenLabel: screen.label,
          buildRevision: options.buildRevision(),
          ...(code === null ? {} : { errorReference: code }),
          idempotencyKey,
        });
        sending = false;
        send.disabled = false;
        if ((reply.status === 201 || reply.status === 200) && reply.body !== null) {
          const created = reply.body.request;
          close();
          showConsoleToast("Sent. We will reply here and by email.");
          openMyRequests(created.id);
          return;
        }
        const message = (reply.body as { message?: string } | null)?.message;
        general.textContent = failureSentence(reply.status, message);
        general.hidden = false;
      })();
    });
    titleInput.focus();
  }

  // ---- My requests --------------------------------------------------------------------------------------------------------

  function closePage(): void {
    loadToken += 1;
    page?.remove();
    page = null;
    selectedId = null;
    list = [];
    detail = null;
    void refresh();
  }

  function openMyRequests(requestId?: string): void {
    closeSheet();
    page?.remove();
    selectedId = requestId ?? null;
    const root = make("section", "support-page");
    root.dataset["testid"] = "support-page";
    root.setAttribute("aria-label", "My requests");
    const head = make("div", "support-page-head");
    const back = make("button", "btn-secondary btn-compact", "‹ Back");
    back.type = "button";
    back.dataset["testid"] = "support-back";
    const h1 = make("h1", "support-page-title", "My requests");
    h1.tabIndex = -1;
    head.append(back, h1);
    const content = make("div", "support-page-body");
    content.dataset["testid"] = "support-page-body";
    root.append(head, content);
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePage();
    });
    back.addEventListener("click", closePage);
    // Under the header, wherever the header ends (a banner may sit below it).
    const header = document.querySelector<HTMLElement>(".topbar");
    if (header !== null) root.style.setProperty("--support-page-top", `${Math.round(header.getBoundingClientRect().bottom)}px`);
    document.body.append(root);
    page = root;
    h1.focus();
    content.append(make("p", "support-help", "Loading..."));
    void loadList(content);
  }

  async function loadList(content: HTMLElement): Promise<void> {
    const token = ++loadToken;
    const reply = await options.request<{ requests: CustomerRequestSummaryDto[]; unreadReplies: number }>("GET", "");
    if (token !== loadToken || page === null) return;
    if (reply.status !== 200 || reply.body === null) {
      content.replaceChildren(failureBlock(() => void loadList(content)));
      return;
    }
    list = reply.body.requests;
    if (list.length === 0) {
      content.replaceChildren(emptyBlock());
      return;
    }
    if (selectedId === null || !list.some((item) => item.id === selectedId)) selectedId = list[0]?.id ?? null;
    drawColumns(content);
    if (selectedId !== null) await loadDetail(content, selectedId);
  }

  function failureBlock(retry: () => void): HTMLElement {
    const box = make("div", "failure-state support-failure");
    box.setAttribute("role", "alert");
    box.append(make("span", "failure-state-symbol", "!"), make("span", "failure-state-text", "Your requests could not load."));
    const again = make("button", "btn-secondary btn-compact failure-state-retry", "Try again");
    again.type = "button";
    again.addEventListener("click", retry);
    box.append(again);
    return box;
  }

  function emptyBlock(): HTMLElement {
    const box = make("div", "support-empty");
    box.dataset["testid"] = "support-empty";
    box.append(make("b", "support-empty-title", "No requests yet"), make("p", "support-help", "Use Get help in the header when you need us."));
    const action = make("button", "btn-primary", "Report a problem");
    action.type = "button";
    action.dataset["testid"] = "support-empty-report";
    action.addEventListener("click", () => {
      closePage();
      openForm("problem");
    });
    box.append(action);
    return box;
  }

  function drawColumns(content: HTMLElement): void {
    const now = new Date();
    const listCol = make("div", "support-list");
    listCol.dataset["testid"] = "support-list";
    for (const item of list) {
      const row = make("button", "support-list-row");
      row.type = "button";
      row.dataset["testid"] = "support-list-row";
      row.dataset["requestId"] = item.id;
      row.setAttribute("aria-current", String(item.id === selectedId));
      const top = make("span", "support-list-top");
      top.append(make("b", "support-list-title", item.title), chipNode(customerChip(item.status)));
      row.append(top, make("span", "support-help", listWhen(item.lastActivityAt, item.hasNewReply, now)));
      row.addEventListener("click", () => {
        selectedId = item.id;
        for (const other of Array.from(listCol.children)) other.setAttribute("aria-current", String((other as HTMLElement).dataset["requestId"] === item.id));
        void loadDetail(content, item.id);
      });
      listCol.append(row);
    }
    const thread = make("div", "support-thread");
    thread.dataset["testid"] = "support-thread";
    content.replaceChildren(listCol, thread);
  }

  async function loadDetail(content: HTMLElement, id: string): Promise<void> {
    const token = ++loadToken;
    const thread = content.querySelector<HTMLElement>(".support-thread");
    if (thread === null) return;
    thread.replaceChildren(make("p", "support-help support-pad", "Loading..."));
    const reply = await options.request<{ request: CustomerRequestDetailDto }>("GET", `/${encodeURIComponent(id)}`);
    if (token !== loadToken || page === null || selectedId !== id) return;
    if (reply.status !== 200 || reply.body === null) {
      thread.replaceChildren(failureBlock(() => void loadDetail(content, id)));
      return;
    }
    detail = reply.body.request;
    drawThread(content, thread, detail);
    // Opening a request read its replies; the list row and the menu badge drop the "new" mark.
    const row = content.querySelector<HTMLElement>(`.support-list-row[data-request-id="${CSS.escape(id)}"]`);
    row?.classList.remove("support-list-row-new");
    void refresh();
  }

  function drawThread(content: HTMLElement, thread: HTMLElement, current: CustomerRequestDetailDto): void {
    const head = make("div", "support-thread-head");
    const titles = make("div", "support-thread-titles");
    titles.append(make("h2", "support-thread-title", current.title));
    const sub = make("p", "support-help", `${typeWord(current.requestType)}, sent ${formatWhen(current.createdAt)}. `);
    if (current.errorReference !== null) sub.append("Code ", make("span", "support-ref", current.errorReference));
    titles.append(sub);
    head.append(titles, chipNode(customerChip(current.status)));

    const messages = make("div", "support-messages");
    messages.dataset["testid"] = "support-messages";
    for (const message of current.messages) {
      const bubble = make("div", `support-bubble support-bubble-${message.from}`);
      bubble.dataset["testid"] = `support-message-${message.from}`;
      const who = make("div", "support-bubble-who");
      who.append(make("b", undefined, message.authorLabel), ` ${formatWhen(message.createdAt)}`);
      if (message.channel === "email") who.append(" by email");
      // Plain text only, line breaks kept by CSS (white-space: pre-wrap), never markup.
      bubble.append(who, make("div", "support-bubble-text", message.body));
      messages.append(bubble);
    }

    const composer = make("div", "support-composer");
    const input = make("input", "field-input");
    input.type = "text";
    input.maxLength = BODY_MAX;
    input.inputMode = "text";
    input.placeholder = current.status === "closed" ? "Write to reopen this request" : "Write a reply";
    input.setAttribute("aria-label", "Write a reply");
    input.dataset["testid"] = "support-reply";
    const send = make("button", "btn-primary", "Send");
    send.type = "button";
    send.dataset["testid"] = "support-reply-send";
    const error = make("p", "support-error support-error-general");
    error.hidden = true;
    error.setAttribute("role", "alert");
    error.dataset["testid"] = "support-reply-error";
    composer.append(input, send);

    let sending = false;
    const submit = (): void => {
      const text = input.value.trim();
      if (text === "" || sending) return;
      sending = true;
      send.disabled = true;
      error.hidden = true;
      void (async () => {
        const reply = await options.request<{ message: unknown }>("POST", `/${encodeURIComponent(current.id)}/messages`, {
          body: text,
          idempotencyKey: crypto.randomUUID(),
        });
        sending = false;
        send.disabled = false;
        if ((reply.status === 201 || reply.status === 200) && reply.body !== null) {
          input.value = "";
          if (selectedId === current.id) await loadList(content);
          return;
        }
        error.textContent = failureSentence(reply.status, (reply.body as { message?: string } | null)?.message);
        error.hidden = false;
      })();
    };
    send.addEventListener("click", submit);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") submit();
    });
    thread.replaceChildren(head, messages, composer, error);
    messages.scrollTop = messages.scrollHeight;
  }

  // ERRTRACK1's Report this on the error notice: the form opens with the notice's code (read when the form draws).
  window.addEventListener("support:report-problem", () => openForm("problem"));

  function reset(): void {
    closeSheet();
    loadToken += 1;
    page?.remove();
    page = null;
    list = [];
    detail = null;
    selectedId = null;
    summary = { unreadReplies: 0, statusUrl: null, supportEmail: null, email: null };
    drawBadge();
  }

  return {
    openSheet: () => {
      // The count and the links are read when the sheet opens, so it never shows a stale "1 new reply".
      void refresh().finally(() => openSheet());
    },
    openMyRequests,
    openForm,
    openPrefilled: (type, prefill) => {
      void refresh().finally(() => openForm(type, prefill));
    },
    refresh,
    reset,
  };
}
